import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import Landing from "./pages/Landing";
import BottomNav from "./components/BottomNav";
import AuthGuard from "./components/AuthGuard";
import BetaBanner from "./components/BetaBanner";
import ErrorBoundary from "./components/ErrorBoundary";
import ThemeMount from "./components/ThemeMount";
import ScrollToTop from "./components/ScrollToTop";
import { MontessoriThemeProvider } from "./contexts/ThemeContext";
import { FloatingLeaves } from "./components/MontessoriDecoration";

// Route-level code splitting — keeps initial bundle small.
const Home = lazy(() => import("./pages/Home"));
const Activities = lazy(() => import("./pages/Activities"));
const SensitivePeriods = lazy(() => import("./pages/SensitivePeriods"));
const Environment = lazy(() => import("./pages/Environment"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const MilestoneDetail = lazy(() => import("./pages/MilestoneDetail"));
const Journal = lazy(() => import("./pages/Journal"));
const Insights = lazy(() => import("./pages/Insights"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Auth = lazy(() => import("./pages/Auth"));
const Profile = lazy(() => import("./pages/Profile"));
const Settings = lazy(() => import("./pages/Settings"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const PrivacyPage = lazy(() => import("./pages/Legal").then(m => ({ default: m.PrivacyPage })));
const TermsPage = lazy(() => import("./pages/Legal").then(m => ({ default: m.TermsPage })));

const queryClient = new QueryClient();

const RouteFallback = () => (
  <div className="min-h-[60vh] flex items-center justify-center">
    <div className="h-6 w-6 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner
        position="bottom-center"
        toastOptions={{
          className: 'glass-lift !bg-card/80 !text-foreground !border-border',
        }}
      />
      <BrowserRouter>
        <ErrorBoundary>
          <MontessoriThemeProvider>
            <ThemeMount />
            <ScrollToTop />
            <FloatingLeaves />
            <BetaBanner />
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/" element={<Landing />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/home" element={<AuthGuard><Home /></AuthGuard>} />
                <Route path="/onboarding" element={<AuthGuard><Onboarding /></AuthGuard>} />
                <Route path="/milestone/:id" element={<AuthGuard><MilestoneDetail /></AuthGuard>} />
                <Route path="/journal" element={<AuthGuard><Journal /></AuthGuard>} />
                <Route path="/insights" element={<AuthGuard><Insights /></AuthGuard>} />
                <Route path="/profile" element={<AuthGuard><Profile /></AuthGuard>} />
                <Route path="/settings" element={<AuthGuard><Settings /></AuthGuard>} />
                <Route path="/activities" element={<AuthGuard><Activities /></AuthGuard>} />
                <Route path="/sensitive-periods" element={<AuthGuard><SensitivePeriods /></AuthGuard>} />
                <Route path="/environment" element={<AuthGuard><Environment /></AuthGuard>} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/unsubscribe" element={<Unsubscribe />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
            <BottomNav />
          </MontessoriThemeProvider>
        </ErrorBoundary>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
